import {useCallback, useEffect, useRef, useState} from 'react'
import {Dialog, Link, Spinner} from '@primer/react'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {StackIcon, type IconProps} from '@primer/octicons-react'
import {StackOverlay} from '@github-ui/pull-requests/components/StackOverlay'
import {PageDataContextProvider} from '@github-ui/pull-request-page-data-tooling/page-data-context'
import {clsx} from 'clsx'
import styles from './StackBadge.module.css'
import {useStackData} from './use-stack-data'

interface StackBadgeProps {
  pullRequestNumber: number
  stackPosition: number | null
  stackSize: number | null
  repoNameWithOwner: string
  size?: IconProps['size']
  appearance?: 'default' | 'link'
}

interface StackOverlayDialogProps {
  repoNameWithOwner: string
  pullRequestNumber: number
  returnFocusRef: React.RefObject<HTMLButtonElement | null>
  onClose: () => void
}

function StackOverlayDialog({repoNameWithOwner, pullRequestNumber, returnFocusRef, onClose}: StackOverlayDialogProps) {
  const {data, isLoading, error} = useStackData(repoNameWithOwner, pullRequestNumber)
  const stack = data?.stack ?? null
  const currentEntryRef = useRef<HTMLAnchorElement>(null)

  // The dialog's focus trap runs before the stack data finishes loading, so it can't use the
  // current entry as its initialFocusRef target. Focus it once it renders to scroll the current entry into view.
  useEffect(() => {
    if (stack) currentEntryRef.current?.focus()
  }, [stack])

  return (
    <Dialog
      title={
        stack && isFeatureEnabled('pull_requests_stacks_view_ui') ? (
          <Link
            inline
            className={styles.StackTitleLink}
            href={`/${stack.repositoryOwnerLogin}/${stack.repositoryName}/stack/${stack.number}`}
          >
            Stack #{stack.number}
          </Link>
        ) : (
          `Stack ${stack ? `#${stack.number}` : ''}`
        )
      }
      width="large"
      returnFocusRef={returnFocusRef}
      initialFocusRef={currentEntryRef}
      onClose={onClose}
      renderBody={({children}) => <Dialog.Body className="p-0">{children}</Dialog.Body>}
    >
      {isLoading && (
        <div className="d-flex flex-justify-center flex-items-center p-4">
          <Spinner size="medium" />
        </div>
      )}
      {error && (
        <div className="d-flex flex-justify-center flex-items-center p-4">
          {/* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */}
          <p className="text-center color-text-danger">Failed to load stack data.</p>
        </div>
      )}
      {stack && (
        // StackOverlay reads from the PageDataContextProvider (for its feedback URL);
        // the /pulls dashboard doesn't use this context elsewhere, so we supply the current PR's base URL here.
        <PageDataContextProvider basePageDataUrl={`/${repoNameWithOwner}/pull/${pullRequestNumber}`}>
          <StackOverlay
            stack={stack}
            pullRequestNumber={pullRequestNumber}
            baseBranchText={stack.baseBranch}
            showShortcutGuidance={false}
            showStackMutationButtons={false}
            currentEntryRef={currentEntryRef}
          />
        </PageDataContextProvider>
      )}
    </Dialog>
  )
}

export function StackBadge({
  pullRequestNumber,
  stackPosition,
  stackSize,
  repoNameWithOwner,
  size = 16,
  appearance = 'default',
}: StackBadgeProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const badgeRef = useRef<HTMLButtonElement>(null)

  const handleClose = useCallback(() => {
    setIsDialogOpen(false)
  }, [])

  if (stackPosition === null || stackSize === null || stackPosition === undefined || stackSize === undefined) {
    return null
  }

  return (
    <>
      <button
        type="button"
        className={clsx(styles.badge, appearance === 'link' && styles.link)}
        onClick={() => setIsDialogOpen(true)}
        ref={badgeRef}
        aria-haspopup="dialog"
        aria-expanded={isDialogOpen}
        aria-label={`Pull request stack, position ${stackPosition} of ${stackSize}`}
      >
        <StackIcon size={size} />
        {stackPosition}/{stackSize}
      </button>
      {isDialogOpen && (
        <StackOverlayDialog
          repoNameWithOwner={repoNameWithOwner}
          pullRequestNumber={pullRequestNumber}
          returnFocusRef={badgeRef}
          onClose={handleClose}
        />
      )}
    </>
  )
}
